import { cn } from '@/lib/cn';
import { type TextareaHTMLAttributes, useMemo, useRef } from 'react';

interface VarInfo {
  defaultValue: string;
  type?: 'string' | 'number' | 'enum';
}

interface Props extends Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'value' | 'onChange'> {
  value: string;
  onChange: (v: string) => void;
  /**
   * key → 변수 메타. 본문에 등장하지만 이 맵에 없거나 defaultValue 가 빈 문자열이면
   * "미정의" 로 간주하고 주황 outline 으로 강조.
   */
  varInfo?: Record<string, VarInfo>;
}

interface Segment {
  kind: 'text' | 'var';
  value: string;
  /** value 안에서의 시작 char offset — React key 와 click → select 양쪽에 사용. */
  start: number;
  /** kind='var' 일 때만. */
  key?: string;
}

function tokenize(value: string): Segment[] {
  const out: Segment[] = [];
  let lastIdx = 0;
  const re = /\{\{(\w+)\}\}/g;
  let m: RegExpExecArray | null = re.exec(value);
  while (m !== null) {
    if (m.index > lastIdx) {
      out.push({ kind: 'text', value: value.slice(lastIdx, m.index), start: lastIdx });
    }
    out.push({ kind: 'var', value: m[0], key: m[1], start: m.index });
    lastIdx = m.index + m[0].length;
    m = re.exec(value);
  }
  if (lastIdx < value.length) {
    out.push({ kind: 'text', value: value.slice(lastIdx), start: lastIdx });
  }
  return out;
}

function typeLabel(t: 'string' | 'number' | 'enum'): string {
  if (t === 'number') return '숫자';
  if (t === 'enum') return '선택지';
  return '문자열';
}

/**
 * `{{key}}` 변수를 강조하는 textarea — 투명 textarea 위에 강조 마크업 div 를 absolute 로 겹쳐 그림.
 *
 * 레이어 순서: textarea (아래) → highlight div (위, pointer-events:none).
 * 변수 span 만 개별 pointer-events:auto 로 hover/click 캐치 → tooltip + 클릭 시 변수 전체 select.
 */
export function HighlightedTextarea({ value, onChange, className, varInfo, ...rest }: Props) {
  const taRef = useRef<HTMLTextAreaElement>(null);
  const hiRef = useRef<HTMLDivElement>(null);

  const syncScroll = () => {
    if (taRef.current && hiRef.current) {
      hiRef.current.scrollTop = taRef.current.scrollTop;
      hiRef.current.scrollLeft = taRef.current.scrollLeft;
    }
  };

  // trailing newline 보강 — 마지막 빈 줄이 collapse 되지 않도록.
  const safeValue = value.endsWith('\n') ? `${value} ` : value;
  const segments = useMemo(() => tokenize(safeValue), [safeValue]);

  const selectVar = (start: number, length: number) => {
    const ta = taRef.current;
    if (!ta) return;
    ta.focus();
    ta.setSelectionRange(start, start + length);
  };

  const shared =
    'absolute inset-0 px-3 py-2 text-sm font-mono leading-relaxed whitespace-pre-wrap wrap-break-word';

  return (
    <div className={cn('relative', className)}>
      <textarea
        ref={taRef}
        {...rest}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onScroll={syncScroll}
        spellCheck={false}
        className={cn(
          shared,
          'bg-transparent text-transparent caret-foreground resize-none border-0',
          'placeholder:text-foreground-muted',
          'focus:outline-none focus-visible:outline-none focus-visible:ring-0',
        )}
      />
      <div
        ref={hiRef}
        aria-hidden
        className={cn(
          shared,
          'pointer-events-none overflow-y-auto overflow-x-hidden text-foreground',
        )}
      >
        {segments.map((seg) => {
          if (seg.kind === 'text') {
            return <span key={`t:${seg.start}`}>{seg.value}</span>;
          }
          const k = seg.key as string;
          const info = varInfo?.[k];
          const hasDefault = !!info && info.defaultValue.length > 0;
          const t = info?.type ?? 'string';
          const tooltip = info
            ? `기본값: ${info.defaultValue || '(미설정)'} · 타입: ${typeLabel(t)}`
            : '미정의 변수 — 변수 기본값 영역에 추가하세요';
          return (
            <span
              key={`v:${seg.start}`}
              title={tooltip}
              onMouseDown={(e) => {
                // click 보다 먼저 발화 — 기본 텍스트 선택 동작 차단 후 변수 전체 선택.
                e.preventDefault();
                selectVar(seg.start, seg.value.length);
              }}
              className={cn(
                'rounded px-0.5 cursor-text pointer-events-auto',
                hasDefault
                  ? 'bg-accent/15 text-accent'
                  : 'bg-orange-500/15 text-orange-600 dark:text-orange-400 outline outline-orange-500/40',
              )}
            >
              {seg.value}
            </span>
          );
        })}
      </div>
    </div>
  );
}
